import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import duration from 'dayjs/plugin/duration';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/de';
import { DATE_FORMATS } from '@callmart/types';
import { appTimezone } from '../config';

// Extend dayjs with all required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(duration);
dayjs.extend(customParseFormat);

// Set locale to German
dayjs.locale('de');

// Set default timezone to application timezone (Europe/Berlin)
dayjs.tz.setDefault(appTimezone);

/**
 * Standard date/time format strings
 * Available as a separate import: import { DATE_FORMATS } from '@callmart/types'
 * See types/src/common/DateFormats.ts for the constants
 */

/**
 * Utility functions for slot date handling
 * Slots are stored in UTC in the database but should be displayed in local timezone
 */

/**
 * Formats a slot date from UTC to local timezone
 * @param slotDate - Date from database (UTC)
 * @param format - Format string (default: TIME format 'HH:mm')
 * @returns Formatted date string in local timezone
 */
export const formatSlotTime = (slotDate: string | Date, format: string = DATE_FORMATS.TIME): string => {
    if (!slotDate) {
        return '';
    }
    return dayjs.utc(slotDate).tz(appTimezone).format(format);
};

/**
 * Formats a slot date to get the day number
 * @param slotDate - Date from database (UTC)
 * @returns Day number in local timezone (e.g., '23')
 */
export const formatSlotDay = (slotDate: string | Date): string => {
    return dayjs.utc(slotDate).tz(appTimezone).format(DATE_FORMATS.DAY_ONLY);
};

/**
 * Converts UTC slot date to local timezone dayjs object
 * @param slotDate - Date from database (UTC), dayjs object, or undefined/null
 * @returns Dayjs object in local timezone (Europe/Berlin), or current Berlin time if slotDate is undefined/null
 */
export const slotToLocalTime = (slotDate?: string | Date | Dayjs | null) => {
    if (!slotDate) {
        return dayjs.utc().tz(appTimezone);
    }
    // If already a dayjs object, return as-is (already in correct timezone)
    if (dayjs.isDayjs(slotDate)) {
        return slotDate;
    }
    return dayjs.utc(slotDate).tz(appTimezone);
};

/**
 * Formats a date-only field (no time component) consistently
 * Use this for fields like startDate, endDate, birthDate, holidayDate
 * @param date - Date string from database (e.g., "2024-01-15")
 * @param format - Format string (default: GERMAN_DATE format 'DD.MM.YYYY')
 * @returns Formatted date string
 */
export const formatDateOnly = (date?: string | Date | null, format: string = DATE_FORMATS.DATE): string => {
    if (!date) {
        return '';
    }
    return dayjs.utc(date).tz(appTimezone).format(format);
};

export default dayjs;

export type { Dayjs };
